import { useNavigate } from 'react-router';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { notifyError } from '@/core/notify/notify';
import { filingService } from '@/core/services/filing';
import type { CompaniesHouseDataset, CompanyOfficerRow } from '@/core/models/filing.models';
import { guardClient, useLiveState, useOnInit, useSelectedClient } from './shared';
import './Integrations.scss';

/** "Company 123, last fetched from Companies House: <date>", shared by the three integration screens. */
export function fetchedLabel(result: CompaniesHouseDataset<unknown>): string {
  const company = result.companyNumber ? `Company ${result.companyNumber}, ` : '';
  return `${company}last fetched from Companies House: ${result.fetchedOn}`;
}

const columns: DataTableColumn<CompanyOfficerRow>[] = [
  { id: 'name', header: 'Name', cell: (row) => row.name },
  { id: 'address', header: 'Address', headerClassName: 'w-200', cellClassName: 'w-200', cell: (row) => row.address },
  { id: 'role', header: 'Role', cell: (row) => row.role },
  { id: 'nationality', header: 'Nationality', cell: (row) => row.nationality },
  { id: 'residence', header: 'Country of Residence', cell: (row) => row.residence },
  { id: 'appointed', header: 'Appointed on ', cell: (row) => row.appointed },
  { id: 'resigned', header: 'Resigned on', cell: (row) => row.resigned },
  { id: 'occupation', header: 'Occupation', cell: (row) => row.occupation },
];

/** Company officers from Companies House for the selected client (Angular TaxreturnIntegrationsComponent). */
export function TaxreturnIntegrations() {
  const navigate = useNavigate();
  const client = useSelectedClient();
  const [s, set] = useLiveState<{ dataSource: CompanyOfficerRow[]; fetchedLabel: string }>({
    dataSource: [],
    fetchedLabel: '',
  });

  useOnInit(() => {
    if (!guardClient(client, navigate)) {
      return;
    }
    filingService.getOfficers(client.id).then(
      (result) => {
        set({ dataSource: result.items, fetchedLabel: fetchedLabel(result) });
        if (result.refreshError) {
          notifyError(`Showing the last fetched data. ${result.refreshError}`);
        }
      },
      (error) => notifyError(error),
    );
  });

  return (
    <SideMenuPage menu="integration" contentClassName="tr-integrations">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <p className="subtitle">List of Company Officers : {s.fetchedLabel}</p>
          </div>
        </div>
        <div className="mat-table-wrapper no-hover">
          <DataTable stickyHeader columns={columns} rows={s.dataSource} rowKey={(_, index) => index} />
        </div>
      </div>
    </SideMenuPage>
  );
}
